import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ClipboardCheck } from 'lucide-react';
import { api } from '../api/cliente.js';
import { Pill, type TomPill } from '../componentes/ui/Pill.js';

interface Solicitacao {
  id: string;
  ip: string;
  ptrAntigo: string | null;
  ptrNovo: string;
  status: 'PENDENTE' | 'APROVADA' | 'REJEITADA' | 'PUBLICADA';
  motivoRejeicao: string | null;
  criadoEm: string;
  revisadoEm: string | null;
}

const STATUS: Record<Solicitacao['status'], { rotulo: string; tom: TomPill; ajuda: string }> = {
  PENDENTE: {
    rotulo: 'Aguardando',
    tom: 'aviso',
    ajuda: 'A Consult Networks ainda vai revisar este pedido.',
  },
  PUBLICADA: {
    rotulo: 'Publicada',
    tom: 'ok',
    ajuda: 'Aprovada e já no ar. Pode levar alguns minutos para propagar.',
  },
  APROVADA: { rotulo: 'Aprovada', tom: 'ok', ajuda: 'Aprovada, aguardando publicação.' },
  REJEITADA: { rotulo: 'Rejeitada', tom: 'erro', ajuda: 'O pedido não foi aceito.' },
};

export function MinhasSolicitacoes() {
  const { data: solicitacoes = [], isLoading } = useQuery({
    queryKey: ['solicitacoes'],
    queryFn: () => api.get<Solicitacao[]>('/solicitacoes'),
    refetchInterval: 60_000,
  });

  if (isLoading) return <Esqueleto />;

  if (solicitacoes.length === 0) {
    return (
      <div className="rounded-card border border-borda bg-superficie px-6 py-14 text-center">
        <ClipboardCheck size={24} className="mx-auto mb-2 text-texto-mudo" />
        <p className="text-[15px] font-extrabold">Nenhuma solicitação</p>
        <p className="mx-auto mt-1 max-w-[400px] text-[13px] text-texto-suave">
          Alterações em blocos que exigem aprovação aparecem aqui até serem revisadas.
        </p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {solicitacoes.map((s) => {
        const info = STATUS[s.status];
        return (
          <li key={s.id} className="rounded-card border border-borda bg-superficie p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-[14px] font-bold">{s.ip}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2 font-mono text-[12.5px]">
                  <span
                    className={
                      s.ptrAntigo ? 'text-texto-suave line-through' : 'italic text-texto-mudo'
                    }
                  >
                    {s.ptrAntigo ?? 'sem reverso'}
                  </span>
                  <ArrowRight size={13} className="shrink-0 text-texto-mudo" />
                  <span className="font-semibold text-marca">{s.ptrNovo}</span>
                </div>
              </div>
              <Pill tom={info.tom}>{info.rotulo}</Pill>
            </div>

            <p className="mt-3 text-[12.5px] text-texto-suave">{info.ajuda}</p>

            {/* O motivo da recusa é o que evita o cliente reenviar igual. */}
            {s.motivoRejeicao && (
              <p className="mt-2 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] text-erro">
                <strong>Motivo:</strong> {s.motivoRejeicao}
              </p>
            )}

            <p className="mt-3 text-[11.5px] text-texto-mudo">
              Enviada em {new Date(s.criadoEm).toLocaleString('pt-BR')}
              {s.revisadoEm && ` · revisada em ${new Date(s.revisadoEm).toLocaleString('pt-BR')}`}
            </p>
          </li>
        );
      })}
    </ul>
  );
}

function Esqueleto() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-32 animate-pulse rounded-card border border-borda bg-superficie" />
      ))}
    </div>
  );
}
